// Team avatar directory — one fetch, cached, shared.
//
// Many surfaces render a teammate's avatar from just a name or a user id
// (kanban owner chips, ongoing-care workload rows, the processing team
// roster, opportunity cards). Rather than thread the uploaded photo URL
// through every one of those list endpoints, we load a tiny directory of
// { user_id, name, image } once (GET /api/team/avatars) and let any site
// resolve a photo locally.
//
// Resolution order: user id (exact) first; display name (normalized) as a
// fallback for sites that only have a name. Ambiguous names — two active
// employees who normalize to the same string — are intentionally NOT
// mapped, so a name lookup never shows the WRONG person's face (it falls
// back to initials). User-id lookups are always unambiguous.
//
// When the directory hasn't loaded yet (or the fetch failed), the
// resolver returns null and callers render their existing initials — so
// this is purely additive and never blocks a render.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import { api, type TeamAvatar } from "../api";
import { useSession } from "../session";
import {
  buildAvatarLookup,
  EMPTY_LOOKUP,
  type AvatarLookup,
} from "./user-avatar-directory-helpers";

export type { AvatarLookup } from "./user-avatar-directory-helpers";

const UserAvatarContext = createContext<AvatarLookup | null>(null);

export function UserAvatarProvider({ children }: PropsWithChildren) {
  const { status } = useSession();
  const [avatars, setAvatars] = useState<TeamAvatar[]>([]);

  useEffect(() => {
    if (status !== "signed_in") {
      setAvatars([]);
      return;
    }
    let cancelled = false;
    api
      .getTeamAvatars()
      .then((res) => {
        if (!cancelled) setAvatars(res.avatars);
      })
      .catch((err) => {
        // Non-fatal — sites fall back to initials. Log for visibility.
        console.error("team avatar directory load failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  const value = useMemo(() => buildAvatarLookup(avatars), [avatars]);

  return (
    <UserAvatarContext.Provider value={value}>
      {children}
    </UserAvatarContext.Provider>
  );
}

// Returns the avatar resolver. Safe to call outside the provider (returns
// a no-op resolver) so components don't have to null-check.
export function useUserAvatar(): AvatarLookup {
  return useContext(UserAvatarContext) ?? EMPTY_LOOKUP;
}
